import { Component, Cursor, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Image, Label, Panel } from "@mcleod/components";
import { Alignment, Api, DatePart, DateUtil, StringUtil } from "@mcleod/core";
import { AutogenLayoutAvailableMovementOverview } from "./autogen/AutogenLayoutAvailableMovementOverview";
import { DistanceAlertUtil } from "./DistanceAlertUtil";
import { MovementOfferTabs } from "./MovementOfferTabs";
import { TrailerUtil } from "./TrailerUtil";
import { getDispatchControlValue } from "@mcleod/general";

export class AvailableMovementOverview extends AutogenLayoutAvailableMovementOverview {
    private distanceAlertUtil = new DistanceAlertUtil();

    onLoad(): void | Promise<void> {
        TrailerUtil.setupMaxTrailers(this.textboxCarrierTrailer, this.textboxTrailer2, this.textboxTrailer3);
        this.switchReserved.visible = getDispatchControlValue("enable_remote_driver_choice") === "Y";
    }

    private setupOffersRelationship(movementId, orderId, isSearch) {
        const movementOffers = this.layoutMovementOfferTabs as MovementOfferTabs;
        movementOffers.initialize(orderId, movementId, this.activeRow);
        movementOffers.addLayoutLoadListener(() => {
            if (isSearch && this.sourceMovement) {
                movementOffers.showAndConfigureTrackingTab(this.sourceMovement);
            }
        });
    }

    set stopCountText(value: string) {
        this.labelIntermediateStops.text = value;
        this.layoutMovementOfferTabs.stopCountText = value;
    }

    labelOriginApptRequiredOnDataDisplay(event: DataDisplayEvent) {
        this.labelOriginApptRequired.visible = event.rowData?.get("stop_origin.appt_required") === "Y" && event.rowData?.get("stop_origin.confirmed") === "N";
        this.labelOriginApptConfirmed.visible = event.rowData?.get("stop_origin.appt_required") === "Y" && event.rowData?.get("stop_origin.confirmed") === "Y";
    }

    labelDestApptRequiredOnDataDisplay(event: DataDisplayEvent) {
        this.labelDestApptRequired.visible = event.rowData?.get("stop_dest.appt_required") === "Y" && event.rowData?.get("stop_dest.confirmed") === "N";
        this.labelDestApptConfirmed.visible = event.rowData?.get("stop_dest.appt_required") === "Y" && event.rowData?.get("stop_dest.confirmed") === "Y";
    }

    sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        const isSearch = event.getAction() === DataSourceAction.SEARCH;
        if (isSearch) {
            const orderId = this.sourceMovement.activeRow?.get("orders.id");
            if (orderId != null)
                this.sourceEquipMatchDetail.search({ _parent_link: { model: this.sourceMovement.url, "orders.id": orderId } });
            this.labelTrackingRequired.visible = this.sourceMovement.activeRow?.get("show_tracking_badge", false);

            const originSchedArriveEarly = this.sourceMovement.activeRow?.get("stop_origin.sched_arrive_early");
            const originSchedArriveLate = this.sourceMovement.activeRow?.get("stop_origin.sched_arrive_late");
            if (originSchedArriveLate == null || this.sameDate(originSchedArriveEarly, originSchedArriveLate)) {
                this.labelOriginSchedArriveLateDay.visible = false;
                this.labelOriginSchedArriveLateMonth.visible = false;
                this.labelOriginSchedArriveLateDate.visible = false;
                this.labelOriginTo.visible = false;
                if (originSchedArriveLate == null || DateUtil.timesEqual(originSchedArriveEarly, originSchedArriveLate)) {
                    this.labelOriginTimeLate.visible = false;
                    this.labelOriginDash.visible = false;
                }
            }
            const destSchedArriveEarly: Date = this.sourceMovement.activeRow?.get("stop_dest.sched_arrive_early");
            const destSchedArriveLate: Date = this.sourceMovement.activeRow?.get("stop_dest.sched_arrive_late");
            if (destSchedArriveLate == null || this.sameDate(destSchedArriveEarly, destSchedArriveLate)) {
                this.labelDestSchedArriveLateDay.visible = false;
                this.labelDestSchedArriveLateMonth.visible = false;
                this.labelDestSchedArriveLateDate.visible = false;
                this.labelDestTo.visible = false;
                if (destSchedArriveLate == null || DateUtil.timesEqual(destSchedArriveEarly, destSchedArriveLate)) {
                    this.labelDestTimeLate.visible = false;
                    this.labelDestDash.visible = false;
                }
            }
            if (!this.sourceMovement.activeRow?.isNull("manifest_id")){
                this.panelOrderData.visible = false;
                this.panelManifestData.visible = true;
            }
        }
        this.setupOffersRelationship(this.mainDataSource.activeRow?.get("id"), this.mainDataSource.activeRow?.get("orders.id"), isSearch);

        // Update freight matching override visibility
        this.updateFreightMatchingOverrideVisibility();
    }

    async sourceMovementOnDisplay() {
        const movementId = this.mainDataSource.activeRow?.get("id") as string;
        const orderId = this.mainDataSource.activeRow?.get("orders.id") as string;
        const customerId = this.mainDataSource.activeRow?.get("customer.id") as string;

        const layoutMovementDataFusionSummary = this.layoutMovementDataFusionSummary;
        if (movementId && orderId && customerId) {
            layoutMovementDataFusionSummary.movementId = movementId;
            layoutMovementDataFusionSummary.orderId = orderId;
            layoutMovementDataFusionSummary.customerId = customerId;

            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/movement-datafusion-summary", body).then(response => {
                if (response) {
                    layoutMovementDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutMovementDataFusionSummary.displayingData;
                }
            });
        }
    }

    private sameDate(originSchedArriveEarly: Date, originSchedArriveLate: Date): boolean {
        const early = DateUtil.parseDate(originSchedArriveEarly);
        const late = DateUtil.parseDate(originSchedArriveLate);
        const diff = DateUtil.dateDiff(DatePart.DAY, late, early);
        return diff == 0;
    }

    /** This is an event handler for the afterExecution event of sourceDistanceAlert. */
    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        if (this.distanceAlertUtil.unclearedDistanceAlertsExist(this.sourceDistanceAlert)) {
            const row = this.sourceDistanceAlert.data[this.sourceDistanceAlert.data.length - 1];

            // from checkForDistanceAlerts
            this.billingDistanceWarning.visible = true;
            for (const component of this.panelMovementDistance.components) {
                const label = component as Label;
                if (!StringUtil.isEmptyString(label.field)) {
                    label.color = "warning";
                    this.billingDistanceWarning.color = "warning";
                    this.labelMoveDistance.text = "0.0";
                    this.labelMovementDistance.text = "0.0";
                    this.labelMI.color = "warning";
                    this.labelMI2.color = "warning";
                }
                this.createLabelHoverToDiscover(label, row);
                label.allowTooltip = true;
            }

            this.moveDistanceWarning.visible = this.labelMoveDistance.visible;
            this.labelMoveDistance.color = "warning";
            this.createLabelHoverToDiscover(this.labelMoveDistance, row);
            this.createLabelHoverToDiscover(this.moveDistanceWarning, row);
            this.labelMoveDistance.allowTooltip = true;
            this.moveDistanceWarning.allowTooltip = true;
        } else {
            this.setDistanceWarningComponentsToDefault(this.billingDistanceWarning, this.panelMovementDistance);
            this.setDistanceWarningLabelToDefault(this.labelMoveDistance, this.moveDistanceWarning);
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private setDistanceWarningComponentsToDefault(distanceWarningImage: Image, panelDistance: Panel) {
        distanceWarningImage.visible = false;
        for (const component of panelDistance.components) {
            const label = component as Label;
            if (!StringUtil.isEmptyString(label.field)) {
                label.color = "default";
            }
        }

        this.labelMI.color = "default";
        this.labelMI2.color = "default";
        this.suppressLabelTooltip(panelDistance);
    }

    private suppressLabelTooltip(panelDistance: Panel) {
        for (const component of panelDistance.components) {
            const label = component as Label;
            label.allowTooltip = false;
        }
    }

    private setDistanceWarningLabelToDefault(label: Label, distanceWarningImage: any) {
        label.allowTooltip = false;
        label.color = "default";
        distanceWarningImage.visible = false;
        distanceWarningImage.suppressLabelTooltip = true;
    }

    private createLabelHoverToDiscover(label: Component, row: any) {
        label.tooltipPosition = Alignment.RIGHT;
        const panel = new Panel({ id: "panelHoverToDisplay", maxWidth: 400, cursor: Cursor.DEFAULT }) as Panel;
        const labelOne = new Label({ id: "hoverIdOne", fontBold: true, fontSize: "xlarge", text: `Distance lookup warning`, rowBreak: false });
        const labelTwo = new Label({ id: "hoverIdTwo", text: "" + row?.get("comments"), rowBreak: true });
        panel.add(labelOne);
        panel.add(labelTwo);

        label.tooltipDynamicContent = panel;
    }

    private updateFreightMatchingOverrideVisibility() {
        if (!this.panelFreightMatchingOverride) return;

        // Only show for brokerage movements when Freight Matching is licensed
        const isBrokerage = this.activeRow?.getBoolean("brokerage", false);
        const dfmLicensedAndActive = this.activeRow?.getBoolean("is_fm_licensed_and_active", false);

        this.panelFreightMatchingOverride.visible = isBrokerage && dfmLicensedAndActive;

        // Make read-only when movement is delivered
        const isDelivered = StringUtil.equalsIgnoreCase(this.activeRow?.get("status"), "D");
        if (isDelivered) {
            this.switchFreightMatchingOverride.enabled = false;
        } else {
            this.switchFreightMatchingOverride.enabled = true;
        }
    }

}
